// ===============================================
// File: src/components/Strips/InspirationStrip.jsx
// Drop-in (Pexels)
// Upgrades:
// - Debounced fetch with AbortController (cancel stale requests)
// - SessionStorage cache per query (reduces API calls while iterating)
// - Graceful no-API-key + error states with inline skeletons
// - Auto-scroll pauses when strip not visible (IntersectionObserver)
// - Lazy loading, better a11y, robust dark/light glass
// - Resilient to SSR / undefined window
// ===============================================
import React, { useEffect, useMemo, useRef, useState } from "react";
import SaveToInspirationButton from "./SaveToInspirationButton";

const DEFAULT_HEIGHT = 280;
const CYCLE_MS = 5000;
const PER_PAGE = 24;
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 min in-session

function getIsDark() {
  if (typeof document === "undefined") return true;
  return document.documentElement.classList.contains("dark");
}

function readCache(q) {
  try {
    const raw = sessionStorage.getItem(`pexels:${q}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.ts || Date.now() - parsed.ts > CACHE_TTL_MS) return null;
    return Array.isArray(parsed.photos) ? parsed.photos : null;
  } catch {
    return null;
  }
}

function writeCache(q, photos) {
  try {
    sessionStorage.setItem(
      `pexels:${q}`,
      JSON.stringify({ ts: Date.now(), photos })
    );
  } catch {}
}

export default function InspirationStrip({
  query = "minimal ceramics",
  height = DEFAULT_HEIGHT,
}) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const scrollerRef = useRef(null);
  const ioStopRef = useRef(false); // whether to pause auto-scroll
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const [isDark, setIsDark] = useState(getIsDark());

  // track dark mode class changes
  useEffect(() => {
    if (typeof document === "undefined") return;
    const mo = new MutationObserver(() => setIsDark(getIsDark()));
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => mo.disconnect();
  }, []);

  // pause auto-scroll when scroller not visible
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const ent of entries) ioStopRef.current = !ent.isIntersecting;
      },
      { threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // fetch Pexels with debounce + abort + cache
  useEffect(() => {
    if (typeof window === "undefined") return;
    const API_KEY = import.meta.env.VITE_PEXELS_API_KEY || "";
    const run = async () => {
      setLoading(true);
      setErr("");

      // cache first
      const cached = readCache(query);
      if (cached) {
        setPhotos(cached);
        setLoading(false);
        return;
      }

      // no key guard
      if (!API_KEY) {
        setErr("Missing VITE_PEXELS_API_KEY");
        setLoading(false);
        return;
      }

      // abort previous
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const res = await fetch(
          `https://api.pexels.com/v1/search?query=${encodeURIComponent(query)}&per_page=${PER_PAGE}`,
          { headers: { Authorization: API_KEY }, signal: controller.signal }
        );
        if (!res.ok) throw new Error(`Pexels ${res.status}`);
        const data = await res.json();
        const list = Array.isArray(data.photos) ? data.photos : [];
        setPhotos(list);
        writeCache(query, list);
      } catch (e) {
        if (controller.signal.aborted) return;
        setErr(e?.message || "Failed to load images");
      } finally {
        if (!abortRef.current?.signal.aborted) setLoading(false);
      }
    };

    // small debounce to avoid rapid refetching while typing
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(run, 300);

    return () => {
      clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, [query]);

  // gentle auto-scroll
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const t = setInterval(() => {
      if (ioStopRef.current) return;
      try {
        const max = el.scrollWidth - el.clientWidth;
        if (el.scrollLeft >= max - 10) {
          el.scrollTo({ left: 0, behavior: "smooth" });
        } else {
          el.scrollTo({ left: Math.min(el.scrollLeft + 320, max), behavior: "smooth" });
        }
      } catch {}
    }, CYCLE_MS);
    return () => clearInterval(t);
  }, [photos.length]);

  const items = useMemo(
    () =>
      (photos || []).map((p) => ({
        id: String(p.id),
        source: "Pexels",
        author: p.photographer,
        authorUrl: p.photographer_url,
        url: p.src?.large2x || p.src?.original || p.url,
        thumb: p.src?.medium || p.src?.small || p.src?.tiny,
        query,
      })),
    [photos, query]
  );

  const frameStyle = isDark
    ? {
        background: "rgba(255,255,255,0.06)",
        border: "1px solid rgba(255,255,255,0.18)",
      }
    : {
        background: "rgba(255,255,255,0.70)",
        border: "1px solid rgba(0,0,0,0.08)",
      };

  const skeletonCount = Math.min(8, Math.ceil((PER_PAGE || 12) / 3));
  const innerH = Math.max(140, height - 12);
  const cardW = Math.round(innerH * 1.6);

  return (
    <div
      className="rounded-2xl p-3 shadow"
      style={{
        ...frameStyle,
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
      }}
    >
      {/* tiny status row inside the glass frame */}
      <div className="flex items-center justify-between px-1 pb-2">
        <div className="text-xs opacity-80">
          Inspiration <span className="opacity-60">Powered by Pexels</span>
          {items.length ? <span className="opacity-60"> • {items.length} images</span> : null}
          {loading ? <span className="opacity-60"> • loading…</span> : null}
        </div>
        {err ? (
          <div className="text-xs text-red-500">
            {err === "Missing VITE_PEXELS_API_KEY"
              ? "Add VITE_PEXELS_API_KEY to .env to load images."
              : err}
          </div>
        ) : null}
      </div>

      {/* rounded/clipped scroller with glassy glaze */}
      <div
        className="overflow-x-auto rounded-xl"
        ref={scrollerRef}
        style={{
          height,
          background: isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.55)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          border: isDark
            ? "1px solid rgba(255,255,255,0.12)"
            : "1px solid rgba(0,0,0,0.06)",
        }}
        aria-label={`Inspiration results for ${query}`}
      >
        <div className="flex gap-3 p-3">
          {loading && !items.length
            ? Array.from({ length: skeletonCount }).map((_, i) => (
                <div
                  key={`sk-${i}`}
                  className="relative rounded-xl overflow-hidden shrink-0 animate-pulse bg-black/10"
                  style={{ width: cardW, height: innerH }}
                />
              ))
            : items.map((img) => (
                <figure
                  key={img.id}
                  className="relative rounded-xl overflow-hidden shrink-0"
                  style={{ width: cardW, height: innerH }}
                >
                  <img
                    src={img.thumb}
                    alt={img.author ? `Photo by ${img.author}` : "Pexels image"}
                    className="w-full h-full object-cover"
                    draggable={false}
                    loading="lazy"
                    decoding="async"
                  />
                  <figcaption className="absolute inset-x-0 bottom-0 p-2 text-[11px] text-white/90 bg-gradient-to-t from-black/55 to-transparent">
                    <div className="flex items-center justify-between gap-2">
                      {img.authorUrl ? (
                        <a
                          href={img.authorUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="truncate hover:underline"
                          title={img.author}
                        >
                          {img.author}
                        </a>
                      ) : (
                        <span className="truncate">{img.author || "Pexels"}</span>
                      )}
                      <SaveToInspirationButton image={img} />
                    </div>
                    {img.query && <div className="opacity-80">{`#${img.query}`}</div>}
                  </figcaption>
                </figure>
              ))}
        </div>
      </div>
    </div>
  );
}
